import { createContext, useContext, useState, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'
import Toast from '../design-system/components/Toast'

const NotificationContext = createContext()

let nextId = 0

export function NotificationProvider({ children }) {
  const [toasts, setToasts] = useState([])

  const addToast = useCallback((type, message, duration = 4000) => {
    const id = nextId++
    setToasts(prev => [...prev, { id, type, message, duration }])
  }, [])

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id))
  }, [])

  const notify = useCallback((message, duration) => addToast('info', message, duration), [addToast])
  const success = useCallback((message, duration) => addToast('success', message, duration), [addToast])
  const warn = useCallback((message, duration) => addToast('warning', message, duration), [addToast])
  const error = useCallback((message, duration) => addToast('error', message, duration), [addToast])

  const value = useMemo(() => ({ notify, success, warn, error }), [notify, success, warn, error])

  return (
    <NotificationContext.Provider value={value}>
      {children}
      {createPortal(
        <div className="notification-container">
          {toasts.map(t => (
            <Toast
              key={t.id}
              type={t.type}
              message={t.message}
              duration={t.duration}
              onClose={() => removeToast(t.id)}
            />
          ))}
        </div>,
        document.body
      )}
    </NotificationContext.Provider>
  )
}

export function useNotification() {
  const ctx = useContext(NotificationContext)
  if (!ctx) throw new Error('useNotification must be used within NotificationProvider')
  return ctx
}
